// ============================================================
// SideQuests - canonical "How SideQuests Works" model
//
// SINGLE SOURCE OF TRUTH for the customer-facing process.
//
// There is exactly ONE four-step model. It is consumed by:
//   * HomePage
//   * HowItWorksPage
//   * Footer
//   * the static SEO registry (scripts/site/site-content.mjs mirrors
//     these strings, and a test proves the two agree)
//
// Do NOT reintroduce a competing "5 simple steps" variant.
// `Authorize Payment` is a payment detail described beneath the four
// steps - it is NOT a fifth top-level step.
// ============================================================

export interface HowItWorksStep {
  /** 1-based position. The model has exactly four steps. */
  number: number;
  title: string;
  body: string;
  /** Short label used by the Footer's compact variant. */
  short: string;
}

export const HOW_IT_WORKS_STEPS: HowItWorksStep[] = [
  {
    number: 1,
    title: 'Post a Quest',
    body: 'Tell people what you need done and what the Quest pays.',
    short: 'Post a Quest',
  },
  {
    number: 2,
    title: 'Answer the Call',
    body: 'Interested Questers tell the Poster they want to help.',
    short: 'Answer the Call',
  },
  {
    number: 3,
    title: 'Pick Your Quester',
    body: 'The Poster chooses who they want.',
    short: 'Pick Your Quester',
  },
  {
    number: 4,
    title: 'Get It Done',
    body: 'Complete the Quest, handle payment through SideQuests and build reputation.',
    short: 'Get It Done',
  },
];

/** Headline used above the step list. Exactly four steps. */
export const HOW_IT_WORKS_HEADLINE = 'Get anything done in 4 simple steps';

/** Supporting payment detail shown beneath the four steps. */
export const HOW_IT_WORKS_PAYMENT_NOTE = {
  title: 'Authorize Payment',
  body:
    'Before a standard Quest goes live, SideQuests authorizes the payment through Stripe. ' +
    'The Poster pays an 18% service fee on the listed Quest reward, with a $5.00 minimum fee, ' +
    'added on top. Standard Quest rewards have a $10.00 minimum. Admin accounts pay no ' +
    'SideQuests service fee and can post any positive whole-cent reward. You always see the ' +
    'full total before anything moves. Your card is captured after you approve the ' +
    'completed work. There is no automatic release: if you do not approve it, the payment ' +
    'stays outstanding and the card authorization may expire. Reporting a problem pauses ' +
    'payment.',
  questerLine: 'The Quester earns the full Quest reward - the service fee is paid by the Poster on top.',
};

/**
 * Compact one-line summary. Used where the full model would be too heavy
 * (for example the Town pages). Never invents a step count other than four.
 */
export const HOW_IT_WORKS_ONE_LINE =
  'Post a Quest, Answer the Call, pick your Quester, and get it done.';

/** True when the given string still contains retired step-count phrasing. */
export const RETIRED_STEP_PHRASES = ['5 simple steps', 'five simple steps'];
